// ============================================================
// HARMONIC CHARTER — Library Store (Zustand)
// ============================================================

import { create } from 'zustand';
import { v4 as uuid } from 'uuid';
import type { Song } from '../core/types';
import {
  loadIndex,
  saveIndex,
  loadSong,
  saveSong,
  deleteSong,
  moveSongToFolder,
  createFolder,
  renameFolder,
  deleteFolder,
  reorderFolders,
  searchSongs,
  type LibraryIndex,
  type SongMeta,
  type Folder,
} from '../core/library';
import { makeDefaultSong } from './songStore';
import { pushSong, deleteSongFromCloud, updateSongFolderAssignment } from '../core/cloudSync';
import {
  pushFolder,
  deleteCloudFolder,
  pushAllFolders,
  pullFolders,
} from '../core/cloudFolders';
import { useAuthStore } from './authStore';

// ------------------------------------------------------------
// LAST OPENED SONG — localStorage helpers
// Persists the most recently opened song ID so we can restore
// it on next load instead of creating a blank Untitled chart.
// ------------------------------------------------------------

const LAST_OPENED_KEY = 'harmonic-charter:last-opened-song';

export function setLastOpenedSongId(id: string): void {
  try { localStorage.setItem(LAST_OPENED_KEY, id); } catch { /* quota */ }
}

export function getLastOpenedSongId(): string | null {
  try { return localStorage.getItem(LAST_OPENED_KEY); } catch { return null; }
}

// ------------------------------------------------------------
// STORE SHAPE
// ------------------------------------------------------------

export interface LibraryStore {
  isOpen:       boolean;
  index:        LibraryIndex;
  searchQuery:  string;
  activeSongId: string | null;

  openLibrary:    () => void;
  closeLibrary:   () => void;
  refreshIndex:   () => void;
  setActiveSongId:(id: string | null) => void;

  setSearchQuery:   (q: string) => void;
  getFilteredSongs: () => SongMeta[];

  openSong:      (id: string, onLoad: (song: Song) => void) => void;
  createSong:    (onLoad: (song: Song) => void, folderId?: string | null) => void;
  duplicateSong: (id: string, onLoad: (song: Song) => void) => void;
  renameSong:    (id: string, title: string) => void;
  deleteSong:    (id: string) => void;
  moveSong:      (songId: string, folderId: string | null) => void;
  saveCurrentSong: (song: Song) => void;

  createFolder:   (name: string) => void;
  renameFolder:   (id: string, name: string) => void;
  deleteFolder:   (id: string) => void;
  reorderFolders: (folderIds: string[]) => void;

  syncFoldersFromCloud: () => Promise<void>;
}

// ------------------------------------------------------------
// STORE
// ------------------------------------------------------------

export const useLibraryStore = create<LibraryStore>((set, get) => ({
  isOpen:       false,
  index:        loadIndex(),
  searchQuery:  '',
  activeSongId: null,

  openLibrary:  () => set({ isOpen: true, index: loadIndex() }),
  closeLibrary: () => set({ isOpen: false, searchQuery: '' }),
  refreshIndex: () => set({ index: loadIndex() }),
  setActiveSongId: (id) => set({ activeSongId: id }),

  setSearchQuery: (q) => set({ searchQuery: q }),

  getFilteredSongs: () => {
    const { index, searchQuery } = get();
    return searchSongs(index, searchQuery);
  },

  openSong: (id, onLoad) => {
    const file = loadSong(id);
    if (!file) return;
    onLoad(file.song);
    setLastOpenedSongId(id);   // FIX: persist last opened
    set({ activeSongId: id, isOpen: false, searchQuery: '' });
  },

  createSong: (onLoad, folderId = null) => {
    const song = makeDefaultSong();
    saveSong(song);
    if (folderId) moveSongToFolder(song.id, folderId);
    onLoad(song);
    setLastOpenedSongId(song.id);   // FIX: persist last opened
    set({ activeSongId: song.id, isOpen: false, index: loadIndex() });
    const user = useAuthStore.getState().user;
    if (user) pushSong(song, user.id, folderId).catch(console.error);
  },

  duplicateSong: (id, onLoad) => {
    const file = loadSong(id);
    if (!file) return;
    const original = get().index.songs.find((s) => s.id === id);
    const newSong: Song = {
      ...file.song,
      id:        uuid(),
      title:     `${file.song.title} (copy)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    saveSong(newSong);
    if (original?.folderId) moveSongToFolder(newSong.id, original.folderId);
    onLoad(newSong);
    setLastOpenedSongId(newSong.id);   // FIX: persist last opened
    set({ activeSongId: newSong.id, isOpen: false, index: loadIndex() });
    const user = useAuthStore.getState().user;
    if (user) pushSong(newSong, user.id, original?.folderId ?? null).catch(console.error);
  },

  renameSong: (id, title) => {
    const file = loadSong(id);
    if (!file) return;
    saveSong({ ...file.song, title, updatedAt: new Date().toISOString() });
    set({ index: loadIndex() });
  },

  deleteSong: (id) => {
    deleteSong(id);
    const { activeSongId } = get();
    set({ index: loadIndex(), activeSongId: activeSongId === id ? null : activeSongId });
    const user = useAuthStore.getState().user;
    if (user) deleteSongFromCloud(id);
  },

  moveSong: (songId, folderId) => {
    moveSongToFolder(songId, folderId);
    set({ index: loadIndex() });
    const user = useAuthStore.getState().user;
    if (user) updateSongFolderAssignment(songId, folderId).catch(console.error);
  },

  saveCurrentSong: (song) => {
    saveSong(song);
    set({ index: loadIndex() });
    const user = useAuthStore.getState().user;
    if (!user) return;
    const index = loadIndex();
    const meta = index.songs.find((s) => s.id === song.id);
    if (meta?.sharedWith) return;
    pushSong(song, user.id).catch(console.error);
  },

  // ── Folder operations ─────────────────────────────────────

  createFolder: (name) => {
    createFolder(name);
    const index = loadIndex();
    set({ index });
    const user = useAuthStore.getState().user;
    if (user) {
      const folder = index.folders[index.folders.length - 1];
      if (folder) pushFolder(folder, index.folders.length - 1).catch(console.error);
    }
  },

  renameFolder: (id, name) => {
    renameFolder(id, name);
    const index = loadIndex();
    set({ index });
    const user = useAuthStore.getState().user;
    if (user) {
      const folder = index.folders.find((f) => f.id === id);
      if (folder) pushFolder(folder, index.folders.indexOf(folder)).catch(console.error);
    }
  },

  deleteFolder: (id) => {
    deleteFolder(id);
    set({ index: loadIndex() });
    const user = useAuthStore.getState().user;
    if (user) deleteCloudFolder(id).catch(console.error);
  },

  reorderFolders: (ids) => {
    reorderFolders(ids);
    const index = loadIndex();
    set({ index });
    const user = useAuthStore.getState().user;
    if (user) pushAllFolders(index.folders).catch(console.error);
  },

  syncFoldersFromCloud: async () => {
    const remoteFolders = await pullFolders();
    if (!remoteFolders.length) return;

    const index = loadIndex();
    const localById = new Map(index.folders.map((f) => [f.id, f]));
    let changed = false;

    for (const remote of remoteFolders) {
      if (localById.has(remote.id)) {
        const local = localById.get(remote.id)!;
        if (local.name !== remote.name) { local.name = remote.name; changed = true; }
      } else {
        index.folders.push({ id: remote.id, name: remote.name, order: index.folders.length });
        changed = true;
      }
    }

    if (changed) saveIndex(index);
    set({ index: loadIndex() });
  },
}));